import React, { FC } from 'react';
import classnames from 'classnames';

import ResearchItem, {
  ResearchTypes
} from 'buyside/components/presentation/ResearchItem/researchItem';
import ResearchItemLoading from 'buyside/components/presentation/ResearchItem/researchItemLoading';
import IdeaIndexJoyride from 'buyside/components/presentation/Joyride/IdeaIndexJoyride/ideaIndexJoyride';
import { IndexAreaLoadingSpinner } from 'buyside/components/presentation/IndexPage/IndexPage';
import IndexPaginationNav from 'buyside/components/presentation/IndexPaginationNav/indexPaginationNav';
import { IdeaIndexJoyrideTarget } from 'buyside/components/presentation/Joyride/IdeaIndexJoyride/ideaIndexJoyrideHelper';

import { useIsMobile, BREAKPOINTS } from 'buyside/util/hooks/useIsMobile';

import { IdeaIndexCurrentUserQuery, IdeaIndexListQuery, Maybe } from 'interfaces/graphql-typings';

import styles from 'buyside/components/presentation/IdeaIndexPage/ideaIndexPageStyles.scss';

const ideaIndexScrollToTop = (): void =>
  document.getElementById('ideaIndexPageHeader')?.scrollIntoView();

const IdeaIndexList: FC<{
  data?: IdeaIndexListQuery;
  edgesLength: number;
  isExact: Maybe<string>;
  isHome: boolean;
  isPublic: boolean;
  loading: boolean;
  page: number;
  queryText: Maybe<string>;
  resultCount: NonNullable<IdeaIndexListQuery['ideasIndexListResultCount']>;
  userData?: IdeaIndexCurrentUserQuery;
}> = ({
  data,
  edgesLength,
  isExact,
  isHome,
  isPublic,
  loading,
  page,
  queryText,
  resultCount,
  userData
}) => {
  const isMobile = useIsMobile(BREAKPOINTS.LARGE);

  return (
    <IndexAreaLoadingSpinner loading={loading} data={data}>
      <div className={classnames({ [styles.homeIdeas]: isHome })}>
        {!loading && !resultCount && (
          <p>No results found. Please try again with a different set of search criteria.</p>
        )}
        {loading && !data && <ResearchItemLoading times={isHome ? 20 : 3} />}
        {data?.ideasIndexList?.edges?.map((edge, idx) => {
          if (!edge?.node) return null;

          const { id, isNonBuyside, phraseCount, user } = edge.node;
          return (
            <div
              key={id}
              data-joyride-target={!isHome && idx === 0 ? IdeaIndexJoyrideTarget.IDEA_CARD : ''}
            >
              {queryText && phraseCount && (
                <div className={styles.ideaPhraseCount}>
                  The {isExact ? 'exact' : 'keywords in the'} phrase <strong>"{queryText}"</strong>{' '}
                  {isExact || phraseCount === 1 ? 'appears ' : 'appear '}
                  {phraseCount}
                  {phraseCount > 1 ? ' times' : ' time'} in this Idea.
                </div>
              )}
              <ResearchItem
                {...edge.node}
                researchType={ResearchTypes.IDEA}
                isPublic={isPublic}
                showUserDetails={
                  userData?.currentUser?.canViewUserDetails ||
                  userData?.currentUser?.id === user?.id ||
                  (!isPublic && isNonBuyside)
                }
                narrow={isHome && isMobile}
              />
              {!isHome && idx === edgesLength - 1 && !isPublic && page === 1 && (
                <IdeaIndexJoyride canWriteIdeas={Boolean(userData?.currentUser?.canWriteIdeas)} />
              )}
            </div>
          );
        })}
      </div>
      <div className={classnames({ [styles.homeIdeas]: isHome })}>
        <IndexPaginationNav
          customScroll={ideaIndexScrollToTop}
          resultCount={resultCount}
          resultLength={edgesLength}
          resultsPerPage={20}
          loading={loading}
        />
      </div>
    </IndexAreaLoadingSpinner>
  );
};

export default IdeaIndexList;
